import React, { useState } from 'react';
import WithDrawer from '../../Dispatch/Common/Drawer/WithDrawer';
import { useNavigate, useLocation } from 'react-router-dom';
import { compose } from 'recompose';
import { withStyles, Button, Divider } from '@material-ui/core';
import { getDataWithQueryString, postData } from '../../../../api/componentAPIs';
import ProDetails from './ProDetails';
import ShipperDetails from './ShipperDetails';
import ShipmentDetails from './ShipmentDetails';
import { withUserProfile } from '../../../../userProfile/userProfileContext';
import ProErrors from './ProErrors';
import ProExcludedReason from './ProExcludedReason';
import CustomerDetails from './CustomerDetails';
import { withSnackbar } from 'notistack';
import { AppLoading } from '../../../../loaders/loaders';
import { PRO_Config, PRO_MODE } from './constants';
import UpdateProStatusOptions from './UpdateProStatusOptions';
import Events from '../../../../utilities/EventEmitter';
import MESSAGES from '../../../../appMessages';
import appConstants from '../../../../appConstants';
import SearchBox from './SearchBox';

const styles = (theme) => ({
  addProContainer: {
    height: '100%'
  },
  moduleHeader: {
    color: theme.HeaderSelectedText,
    borderBottom: `1px solid ${theme.AppBGColor}`,
    fontWeight: 'bold',
    padding: `10px 32px`,
    fontSize: theme.headerTextFontSize
  },
  formContainer: {
    padding: 16,
    height: '80%',
    overflowY: 'auto'
  },
  circularProgress: {
    color: theme.white
  },
  actionButton: {
    textTransform: 'none',
    minWidth: 120,
    margin: '0px 4px',
    '&$actionButtonDisabled': {
      backgroundColor: theme.customInputBGColor,
      color: theme.TextColor,
      cursor: 'not-allowed',
      opacity: 0.8
    },
    '&$actionOutlineDisabled': {
      backgroundColor: theme.headerIconColor,
      color: theme.TextColor
    }
  },
  actionButtonDisabled: {},
  actionOutlineDisabled: {},
  actionContainer: {
    display: 'flex',
    paddingTop: 20,
    paddingRight: 20,
    justifyContent: 'flex-end'
  },
  marginNormal: { margin: 0, width: '100%' },
  inputField: {
    padding: 8,
    fontSize: theme.textFontSize,
    color: theme.TextColor,
    height: 'initial'
  },
  button: {
    width: '15%',
    marginLeft: 4,
    height: 34,
    '&$disabledButton,&$disabledButton:hover': {
      cursor: 'not-allowed',
      pointerEvents: 'all',
      backgroundColor: theme.customInputBGColor,
      color: theme.TextColor,
      opacity: 0.8
    }
  },
  searchBoxWrapper: {
    width: '60%',
    display: 'flex'
  },
  searchContainer: {
    display: 'flex',
    marginBottom: 12
  },
  fullInputBox: {
    width: '100%',
    paddingRight: '8px'
  },
  labelClass: {
    lineHeight: '8px',
    color: theme.TextColor,
    transform: 'translate(14px, 14px) scale(1)',
    fontSize: theme.headerTextFontSize,
    opacity: 0.7
  },
  disabledButton: {
    cursor: 'not-allowed',
    pointerEvents: 'all',
    backgroundColor: theme.iconColor,
    color: theme.white
  },
  divider: {
    backgroundColor: theme.AppBGColor
  },
  errorMessage: {
    color: theme.iconColor,
    textAlign: 'center',
    fontSize: theme.textFontSize,
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    height: '75%'
  }
});

const AddProToPlan = ({ classes, userData, enqueueSnackbar, proMode }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchInputValue, setSearchInputValue] = useState('');
  const [showSearchLoader, setShowSearchLoader] = useState(false);
  const [isAddInProgress, setInProgress] = useState(false);
  const [proData, setProData] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [updateStatus, setUpdatedProStatus] = useState(null);
  const [updateStatusError, setUpdatedProStatusError] = useState('');
  const [proError, setProError] = useState([]);

  const onClose = () => {
    setProData(null);
    navigate(location.pathname, { replace: true });
  };

  const handleSearchChange = (value) => {
    setSearchInputValue(value);
  };

  const onSearch = () => {
    if (!showSearchLoader && searchInputValue) {
      setProData(null);
      setErrorMessage(null);
      setShowSearchLoader(true);
      getDataWithQueryString(
        'GetIBplanningPro',
        `proId=${searchInputValue}&planDate=${userData.userProfile.planDate}&terminalId=${userData.userProfile.terminal.id}`
      ).then(
        (response) => {
          if (response && response.status === 200) {
            if (response.data && Object.keys(response.data).length > 0) {
              setProData(response.data);
              getErrorsBasedOnMode(response.data);
              setShowSearchLoader(false);
            } else {
              handleSearchError({
                message:
                  'No results found for the entered pro number. Please verify the number or contact support.'
              });
            }
          } else {
            handleSearchError(response);
          }
        },
        (error) => {
          handleSearchError(error);
        }
      );
    }
  };

  const handleSearchError = (response) => {
    setProData(null);
    setShowSearchLoader(false);
    const errorMessage =
      response?.response?.data?.error?.message ||
      response?.message ||
      'An error occurred while fetching Pro details';

    setErrorMessage(errorMessage);
  };

  const addShipmentsToPlan = () => {
    if (isAddUpdateButtonDisabled()) {
      return;
    }
    if (!location.state?.ibEtaCutOff) {
      enqueueSnackbar('No ibEtaCutOff found. Please refresh the page and try again.', {
        variant: 'error'
      });
      return;
    }
    setInProgress(true);
    const reqData = {
      pros: [proData.proId],
      planDate: userData.userProfile.planDate,
      terminalId: userData.userProfile.terminal.id,
      ibEtaCutOff: location.state?.ibEtaCutOff
    };

    postData('AddShipmentsToPlan', reqData).then(
      (response) => {
        if (response && response.status >= 200 && response.status < 400) {
          enqueueSnackbar(
            `Pro ${proData.proId} has been added to the plan. Plan update is in progress.`,
            {
              variant: 'success'
            }
          );
          resetData();
        } else {
          handleError(response, 'adding Pro to the plan');
        }
      },
      (error) => {
        handleError(error, 'adding Pro to the plan');
      }
    );
  };
  const handleError = (response, action) => {
    setInProgress(false);
    if (response.statusCode === 403) {
      return;
    }
    const errorMessage =
      response?.response?.data?.error?.message ||
      response?.response?.data?.error?.details ||
      response?.message;
    enqueueSnackbar(errorMessage || `An error occurred while ${action}`, {
      variant: 'error'
    });
  };

  const getErrorsBasedOnMode = (proData) => {
    const proErrors = proData?.[PRO_Config[proMode].proErrors];
    setProError(proErrors);
  };

  const setUpdatedProStatusHandler = (status) => {
    setUpdatedProStatusError('');
    setUpdatedProStatus(status);
  };

  const updateProStatus = () => {
    if (isAddUpdateButtonDisabled()) {
      return;
    }
    if (!updateStatus) {
      setUpdatedProStatusError(MESSAGES.UPDATE_PRO_IN_PLAN.PRO_STATUS_NOT_SELECTED);
      return;
    }
    setInProgress(true);
    const reqData = {
      proId: proData.proId,
      isDelivered: updateStatus === 'isDelivered',
      isDeleted: updateStatus === 'isDeleted',
      terminalId: userData.userProfile.terminal.id
    };
    postData('UpdateShipmentInPlan', reqData).then(
      (response) => {
        if (response && response.status >= 200 && response.status < 400) {
          Events.emit(appConstants.Event.RESTOREIBSTORE);
          enqueueSnackbar(`Pro ${proData.proId} status updated successfully.`, {
            variant: 'success'
          });
          resetData();
        } else {
          handleError(response, 'updating pro status');
        }
      },
      (error) => {
        handleError(error, 'updating pro status');
      }
    );
  };

  const isAddUpdateButtonDisabled = () => {
    return (
      !proData ||
      proError.length > 0 ||
      isAddInProgress ||
      errorMessage ||
      (proMode === PRO_MODE.update && ['Deleted', 'Delivered'].includes(proData.rmaxShipmentStatus))
    );
  };

  const resetData = () => {
    setProData(null);
    setInProgress(false);
    setProError([]);
    setSearchInputValue('');
  };

  return (
    <div className={classes.addProContainer}>
      <header className={classes.moduleHeader}>{PRO_Config[proMode].header}</header>
      <div className={`${classes.formContainer} customScroll`}>
        <SearchBox
          value={searchInputValue}
          onChange={handleSearchChange}
          onSearch={onSearch}
          loading={showSearchLoader}
          disabled={false}
          classes={classes}
          autoFocus
          inputLabel="Pro Number"
          id="ProNumber"
        />
        {showSearchLoader && <AppLoading />}
        {errorMessage && !proData && <div className={classes.errorMessage}>{errorMessage}</div>}
        {proData && !errorMessage && (
          <>
            {PRO_Config[proMode].showProExcludedReason && proError.length === 0 && (
              <ProExcludedReason excludedReasons={proData.excludedReason} />
            )}
            {proError.length > 0 && <ProErrors errors={proError} />}
            {PRO_Config[proMode].showUpdateProStatusOptions && (
              <UpdateProStatusOptions
                updateStatus={updateStatus}
                setUpdatedProStatus={setUpdatedProStatusHandler}
                shipmentStatus={proData.shipmentStatus}
                disableUpdate={['Deleted', 'Delivered'].includes(proData.rmaxShipmentStatus)}
                error={updateStatusError}
              />
            )}
            <CustomerDetails proData={proData} />
            <Divider className={classes.divider} />
            <ProDetails proData={proData} />
            <Divider className={classes.divider} />
            <ShipmentDetails proData={proData} />
            <Divider className={classes.divider} />
            <ShipperDetails proData={proData} />
          </>
        )}
      </div>
      <div className={classes.actionContainer}>
        <Button
          variant="outlined"
          color="primary"
          size="medium"
          onClick={onClose}
          classes={{ root: classes.actionButton }}
          id="closeButton">
          Close
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="medium"
          type="submit"
          onClick={proMode === PRO_MODE.add ? addShipmentsToPlan : updateProStatus}
          disabled={isAddUpdateButtonDisabled()}
          classes={{
            root: classes.actionButton,
            disabled: classes.actionButtonDisabled
          }}>
          {PRO_Config[proMode].buttonLabel}
        </Button>
      </div>
    </div>
  );
};

export default compose(WithDrawer, withStyles(styles), withUserProfile, withSnackbar)(AddProToPlan);
